import React, { useReducer, useContext, useRef, useCallback, useEffect, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { credentialsReducer } from './credentialsReducer';
import { CredentialsActionTypes } from './credentialsActions';
import {
	credentialsInitialState,
	CredentialsContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './credentialsContext';
import { useFetchData } from 'hooks/useFetchData';
import { IDeleteApkFirmwareValidationError, IServerError, IGetPaginatedCredentials, ICredentials } from 'types';
import { AxiosResponse } from 'axios';
import { getPaginatedCredentials, deleteCredentials } from 'backend/credentials';

export const CredentialsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(credentialsReducer, credentialsInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);

	const { refetch, response, loading } = useFetchData<AxiosResponse<ICredentials>, IGetPaginatedCredentials>(
		isMounted.current ? getPaginatedCredentials : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			name: state.searchString ? state.searchString : null,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: CredentialsActionTypes.SET_CREDENTIALS,
				payload: {
					credentials: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		showLoader();
		try {
			await deleteCredentials(state.IDForDeletion);
			const filteredCredentials = state.credentials.credentialItems.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: CredentialsActionTypes.SET_CREDENTIALS,
				payload: {
					credentials: { count: state.credentials.count - 1, credentialItems: filteredCredentials },
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: CredentialsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.credentials]);

	const changeSearchString = useCallback(
		(value: string) => {
			if (allowChangingSearchString) {
				dispatch({
					type: CredentialsActionTypes.CHANGE_SEARCH_STRING,
					payload: {
						searchString: value,
					},
				});
			}
		},
		[allowChangingSearchString],
	);

	const setSortBy = useCallback((sortBy: TSortBy) => {
		dispatch({
			type: CredentialsActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	}, []);

	const setCountOfItems = useCallback((count: TCountOfItems) => {
		dispatch({
			type: CredentialsActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	}, []);

	const changeCurrentPage = useCallback((page: number) => {
		dispatch({
			type: CredentialsActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	}, []);

	const setIDForDeletion = useCallback((value: string) => {
		dispatch({
			type: CredentialsActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
			},
		});
	}, []);

	const resetSelectedControls = useCallback(
		() => dispatch({ type: CredentialsActionTypes.RESET_SELECTED_CONTROLS }),
		[],
	);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CredentialsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<IDeleteApkFirmwareValidationError> | null) => {
		dispatch({
			type: CredentialsActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const setFiltersFromQueryString = useCallback(
		(value: IFilterValues) => {
			dispatch({
				type: CredentialsActionTypes.SET_FILTERS_FROM_QUERY_STRING,
				payload: {
					filters: value,
				},
			});
			isMounted.current = true;
			refetch();
		},
		[isMounted, refetch],
	);

	return (
		<CredentialsContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				resetSelectedControls,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				showErrorModalWithInfo,
				credentials: state.credentials,
				searchString: state.searchString,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</CredentialsContext.Provider>
	);
};
